import React, { useState, useMemo } from "react";
import { formatDateShort } from "../../types/booking";
import { BOOKING_STATUS } from "../../types/booking";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";

const BookingCalendar = ({ bookings, onBookingClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("month");
  const [statusFilter, setStatusFilter] = useState("all");
  const [eventTypeFilter, setEventTypeFilter] = useState("all");

  // Filter bookings
  const filteredBookings = useMemo(() => {
    return bookings.filter((booking) => {
      if (statusFilter !== "all" && booking.status !== statusFilter) {
        return false;
      }
      if (eventTypeFilter !== "all" && booking.eventType !== eventTypeFilter) {
        return false;
      }
      return true;
    });
  }, [bookings, statusFilter, eventTypeFilter]);

  // Get days in current month
  const monthDays = useMemo(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  // Get bookings for a specific day
  const getBookingsForDay = (day) => {
    return filteredBookings.filter((booking) => {
      // Check hennaDate first (when the work is actually done)
      if (booking.hennaDate) {
        const hennaDate =
          booking.hennaDate instanceof Date
            ? booking.hennaDate
            : booking.hennaDate?.toDate
            ? booking.hennaDate.toDate()
            : parseISO(booking.hennaDate);

        if (isSameDay(hennaDate, day)) return true;
      }

      // Fallback to eventDate for existing bookings without hennaDate
      if (!booking.eventDate) return false;

      const eventDate =
        booking.eventDate instanceof Date
          ? booking.eventDate
          : booking.eventDate?.toDate
          ? booking.eventDate.toDate()
          : parseISO(booking.eventDate);

      return isSameDay(eventDate, day);
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      [BOOKING_STATUS.PENDING_DEPOSIT]: "#F59E0B",
      [BOOKING_STATUS.CONFIRMED]: "#3B82F6",
      [BOOKING_STATUS.COMPLETED]: "#10B981",
      [BOOKING_STATUS.CANCELLED]: "#EF4444",
      [BOOKING_STATUS.NO_SHOW]: "#6B7280",
    };
    return colors[status] || "#D4AF7A";
  };

  // Navigation
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Get unique event types
  const eventTypes = useMemo(() => {
    const types = new Set(bookings.map((b) => b.eventType).filter(Boolean));
    return Array.from(types);
  }, [bookings]);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        {/* Month Navigation */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg hover:bg-white/10 transition-colors text-[#F7F5F2]"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="text-xl font-semibold text-[#F7F5F2] min-w-[150px] text-center">
            {format(currentDate, "MMMM yyyy")}
          </h3>
          <button
            onClick={() => navigateMonth(1)}
            className="p-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg hover:bg-white/10 transition-colors text-[#F7F5F2]"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
          >
            <option value="all">All Status</option>
            <option value={BOOKING_STATUS.PENDING_DEPOSIT}>Pending Deposit</option>
            <option value={BOOKING_STATUS.CONFIRMED}>Confirmed</option>
            <option value={BOOKING_STATUS.COMPLETED}>Completed</option>
            <option value={BOOKING_STATUS.CANCELLED}>Cancelled</option>
          </select>

          {eventTypes.length > 0 && (
            <select
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              className="px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
            >
              <option value="all">All Types</option>
              {eventTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Status Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: "#F59E0B" }} />
          <span className="text-[#F7F5F2]/70">Pending</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: "#3B82F6" }} />
          <span className="text-[#F7F5F2]/70">Confirmed</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: "#10B981" }} />
          <span className="text-[#F7F5F2]/70">Completed</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: "#EF4444" }} />
          <span className="text-[#F7F5F2]/70">Cancelled</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-[#0A0A0A] border border-[#D4AF7A]/20 rounded-xl overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b border-[#D4AF7A]/20">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              className="p-3 text-center text-sm font-semibold text-[#F7F5F2]/70 bg-white/5"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {monthDays.map((day) => {
            const dayBookings = getBookingsForDay(day);
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={day.toISOString()}
                className={`min-h-[100px] p-2 border-b border-r border-[#D4AF7A]/10 ${
                  isToday ? "bg-[#D4AF7A]/10" : ""
                }`}
              >
                <div className="text-sm text-[#F7F5F2]/70 mb-1">
                  {format(day, "d")}
                </div>
                <div className="space-y-1">
                  {dayBookings.map((booking) => {
                    const displayTime = booking.hennaTime || booking.eventTime || "";
                    const hasDifferentDate = booking.hennaDate && booking.eventDate &&
                      !isSameDay(
                        booking.hennaDate instanceof Date ? booking.hennaDate : new Date(booking.hennaDate),
                        booking.eventDate instanceof Date ? booking.eventDate : new Date(booking.eventDate)
                      );

                    return (
                      <div
                        key={booking.id}
                        onClick={() => onBookingClick && onBookingClick(booking)}
                        className="text-xs p-1.5 rounded cursor-pointer hover:opacity-80 transition-opacity"
                        style={{
                          backgroundColor: getStatusColor(booking.status),
                          color: "#000000",
                        }}
                        title={`${booking.clientName} - ${booking.eventType}${hasDifferentDate ? " (Henna date differs from event)" : ""}`}
                      >
                        <div className="font-medium truncate flex items-center gap-1">
                          {displayTime}
                          {hasDifferentDate && <span className="text-[8px] opacity-70">*</span>}
                        </div>
                        <div className="truncate opacity-80">{booking.clientName}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info */}
      <div className="flex items-center justify-between text-xs text-[#F7F5F2]/60">
        <span>{filteredBookings.length} booking{filteredBookings.length !== 1 ? "s" : ""} displayed</span>
        <span>Click on an event to view booking details</span>
      </div>
    </div>
  );
};

export default BookingCalendar;
